<template>
  <div class="modal-overlay" @click.self="$emit('close')">
      <div ref="modalRef" class="modal-content">
      <div class="modal-header">
        <h2>{{ $t('tables.batchAddTables') }}</h2>
        <button @click="$emit('close')" class="close-btn">
          <font-awesome-icon icon="fa-times" />
        </button>
      </div>

      <div class="modal-body">
        <div class="two-column-layout">
          <div class="form-column">
            <h3 class="section-title">{{ $t('tables.settings') }}</h3>

            <div class="form-group">
              <label for="namePrefix">{{ $t('tables.namePrefix') }}</label>
              <div class="input-with-icon">
                <font-awesome-icon icon="hashtag" class="input-icon" />
                <input id="namePrefix" v-model="formData.prefix" type="text" class="form-control"
                  :placeholder="$t('tables.namePrefixPlaceholder')" />
              </div>
              <small class="form-text text-muted">{{ $t('tables.namePrefixHelp') }}</small>
            </div>

            <div class="form-row">
              <div class="form-group form-group-half">
                <label for="startNumber">{{ $t('tables.startNumber') }} <span class="required">*</span></label>
                <div class="input-with-icon">
                  <font-awesome-icon icon="arrow-right" class="input-icon" />
                  <input id="startNumber" v-model.number="formData.start_number" type="number" class="form-control"
                    :placeholder="$t('tables.startNumberPlaceholder')" min="1" />
                </div>
              </div>

              <div class="form-group form-group-half">
                <label for="endNumber">{{ $t('tables.endNumber') }} <span class="required">*</span></label>
                <div class="input-with-icon">
                  <font-awesome-icon icon="arrow-left" class="input-icon" />
                  <input id="endNumber" v-model.number="formData.end_number" type="number" class="form-control"
                    :placeholder="$t('tables.endNumberPlaceholder')" min="1" />
                </div>
              </div>
            </div>

            <div class="form-group">
              <label for="tableCapacity">{{ $t('tables.capacity') }}</label>
              <div class="input-with-icon">
                <font-awesome-icon icon="users" class="input-icon" />
                <input id="tableCapacity" v-model.number="formData.capacity" type="number" class="form-control"
                  :placeholder="$t('tables.capacityPlaceholder')" min="0" />
              </div>
            </div>

            <div v-if="error" class="error-message">
              <font-awesome-icon icon="exclamation-circle" class="error-icon" />
              {{ error }}
            </div>
          </div>

          <div class="preview-column">
            <div v-if="!isValidRange" class="empty-preview">
              <font-awesome-icon icon="info-circle" size="2x" />
              <p>{{ $t('tables.enterValidRange') }}</p>
            </div>

            <div v-else class="preview-content">
              <div class="preview-header">
                <h3 class="section-title-small">{{ $t('tables.preview') }}</h3>
                <div class="table-count-badge">
                  <font-awesome-icon icon="table" />
                  <span v-if="$i18n.locale === 'hr'">{{ tableCount }} stolova</span>
                  <span v-else>{{ tableCount }} tables</span>
                </div>
              </div>

              <div class="preview-grid">
                <div v-for="(tableName, index) in previewNames" :key="index" class="table-preview-item">
                  <div class="table-content">
                    <div class="table-icon">
                      <font-awesome-icon icon="table" />
                    </div>
                    <div class="table-name">{{ tableName }}</div>
                    <div v-if="(formData.capacity ?? 0) > 0" class="table-capacity">
                      <font-awesome-icon icon="users" /> {{ formData.capacity }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <div class="action-info" v-if="isValidRange">
            <font-awesome-icon icon="info-circle" class="info-icon" />
            <span v-if="$i18n.locale === 'hr'">Kreirat će se {{ tableCount }} stolova</span>
            <span v-else>{{ tableCount }} tables will be created</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive } from 'vue';
import BaseButton from '@/components/shared/BaseButton.vue';
import { tableApi } from '@/services/api';
import { TableBatchRequest } from '@/types/tables';
import { useI18n } from 'vue-i18n';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { useFormFloatingActions } from '@/composables/useFormFloatingActions';
import { scrollToFirstFormError, stopGuidedFormErrorNavigation } from '@/utils/scrollToFirstFormError';

const modalRef = ref<HTMLElement | null>(null);

const props = defineProps({
  locationId: {
    type: String,
    required: true
  }
});

const emit = defineEmits(['close', 'saved']);
const { t } = useI18n();

const loading = ref(false);
const error = ref<string | null>(null);

const formData = ref<TableBatchRequest>({
  prefix: 'Stol',
  start_number: 1,
  end_number: 10,
  capacity: 0
});

const isValidRange = computed(() => {
  return formData.value.start_number >= 1 &&
    formData.value.end_number >= formData.value.start_number &&
    formData.value.end_number - formData.value.start_number < 100;
});

const tableCount = computed(() => {
  if (!isValidRange.value) return 0;
  return formData.value.end_number - formData.value.start_number + 1;
});

const previewNames = computed(() => {
  if (!isValidRange.value) return [];

  const names = [];
  for (let i = formData.value.start_number; i <= formData.value.end_number; i++) {
    names.push(`${formData.value.prefix} ${i}`);
  }
  return names;
});

const batchFormErrors = reactive<Record<string, string>>({
  start_number: '',
});

function validateBatchForm(): boolean {
  batchFormErrors.start_number = '';
  if (!isValidRange.value) {
    batchFormErrors.start_number = t('tables.invalidRangeError');
    error.value = batchFormErrors.start_number;
    return false;
  }
  error.value = null;
  return true;
}

const saveTables = async () => {
  if (!validateBatchForm()) {
    await scrollToFirstFormError(batchFormErrors, {
      root: modalRef.value,
      fieldOrder: ['start_number', 'end_number', 'namePrefix'],
      fieldSelectorMap: {
        start_number: '#startNumber',
        end_number: '#endNumber',
        namePrefix: '#namePrefix',
      },
      validate: validateBatchForm,
      getErrors: () => batchFormErrors,
    });
    return;
  }

  stopGuidedFormErrorNavigation();

  loading.value = true;
  error.value = null;

  try {
    await tableApi.batchCreateTables(props.locationId, formData.value);
    emit('saved');
  } catch (err: any) {

    error.value = getApiErrorMessage(err, t('tables.errorCreatingTables'));
  } finally {
    loading.value = false;
  }
};

useFormFloatingActions({
  validate: validateBatchForm,
  submit: saveTables,
  cancel: () => emit('close'),
  formErrors: batchFormErrors,
  getSubmitLabel: () => t('tables.createTables'),
  getLoading: () => loading.value,
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 12px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background-color: white;
  z-index: 10;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: #2d3748;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: #6c757d;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #343a40;
}

.modal-body {
  display: flex;
  flex-direction: column;
  height: calc(90vh - 70px);
  overflow: hidden;
  flex: 1;
}

.two-column-layout {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;

}

.form-column {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
  overflow-x: hidden;
  border-right: 1px solid #e9ecef;
  min-height: 0;

}

.preview-column {
  flex: 1;
  padding: 1.25rem;
  overflow: hidden;
  background-color: #f8fafc;
  border-radius: 0 0 12px 0;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;

}

.section-title {
  margin-top: 0;
  margin-bottom: 1.25rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #4a5568;
  position: relative;
  padding-bottom: 0.5rem;
  width: 100%;
}

.section-title:after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 40px;
  height: 3px;
  background-color: #D8670C;
  border-radius: 3px;
}

.section-title-small {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #4a5568;
  position: relative;
  padding-left: 0.25rem;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-row {
  display: flex;
  gap: 1.25rem;
  margin-bottom: 1.25rem;
}

.form-group-half {
  flex: 1;
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #4a5568;
}

.required {
  color: #f56565;
  margin-left: 2px;
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: #718096;
  font-size: 0.9rem;
}

.form-control {
  width: 100%;
  padding: 0.6rem 0.75rem 0.6rem 2.25rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #4a5568;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  transition: all 0.2s ease-in-out;
}

.form-control:focus {
  border-color: #D8670C;
  outline: 0;
  box-shadow: 0 0 0 3px rgba(216, 103, 12, 0.15);
}

.form-text {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.875rem;
}

.text-muted {
  color: #718096;
}

.empty-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #718096;
  background-color: #f7fafc;
  border: 1px dashed #cbd5e0;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
}

.empty-preview p {
  margin-top: 1rem;
  font-size: 1rem;
}

.preview-content {
  display: flex;
  flex-direction: column;
  max-width: 100%;
  flex: 1;
  overflow: hidden;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0 0.25rem;
}

.table-count-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, rgba(50, 57, 74, 0.08) 0%, rgba(35, 40, 48, 0.05) 100%);
  color: #4a5568;
  padding: 0.35rem 0.8rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  width: fit-content;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(74, 85, 104, 0.08);
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  margin-left: auto;
}

.table-count-badge::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #D8670C 0%, rgba(216, 103, 12, 0.3) 100%);
}

.table-count-badge .svg-inline--fa {
  color: #D8670C;
  opacity: 0.9;
  font-size: 0.85rem;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.05));
}

.preview-actions {
  display: flex;
  align-items: center;
}

.preview-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background-color: #f7fafc;
  color: #4a5568;
  border: 1px solid #e2e8f0;
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0.4rem 0.6rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.preview-action-btn:hover {
  background-color: #edf2f7;
  color: #D8670C;
  border-color: rgba(216, 103, 12, 0.2);
}

.action-icon {
  font-size: 0.7rem;
}

.preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(78px, 1fr));
  gap: 0.6rem;
  margin-bottom: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.5rem 0 0.5rem 0;
  width: 100%;
  flex: 1;
}

.preview-grid::-webkit-scrollbar {
  width: 6px;
}

.preview-grid::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.preview-grid::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 10px;
}

.preview-grid::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

.table-preview-item {
  position: relative;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.25s ease;
  height: 80px;
  cursor: default;
  background: linear-gradient(135deg, #32394a 0%, #232830 100%);
  overflow: hidden;
}

.table-preview-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(216, 103, 12, 0.35) 0%, rgba(192, 90, 10, 0.35) 100%);
  clip-path: polygon(0 0, 100% 0, 100% 40%, 0 70%);
  transition: all 0.25s ease;
  z-index: 1;
  opacity: 0.65;
}

.table-preview-item::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.06) 0%, transparent 70%);
  z-index: 2;
}

.table-preview-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
}

.table-preview-item:hover::before {
  clip-path: polygon(0 0, 100% 0, 100% 60%, 0 100%);
  opacity: 0.75;
}

.table-content {
  position: relative;
  height: 100%;
  width: 100%;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
}

.table-icon {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
  opacity: 0.95;
  margin-bottom: 6px;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.15));
  transition: all 0.25s ease;
}

.table-preview-item:hover .table-icon {
  transform: scale(1.08);
  color: rgba(255, 255, 255, 1);
}

.table-name {
  font-weight: 500;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.9);
  padding: 0 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  text-align: center;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.15);
  transition: all 0.25s ease;
  letter-spacing: 0.01em;
}

.table-preview-item:hover .table-name {
  color: rgba(255, 255, 255, 1);
}

.table-capacity {
  font-size: 0.6rem;
  color: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  gap: 0.2rem;
  background-color: rgba(255, 255, 255, 0.08);
  padding: 0.1rem 0.3rem;
  border-radius: 4px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  margin-top: 4px;
  transition: all 0.25s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e9ecef;
  background-color: white;
  z-index: 10;
  flex-shrink: 0;
}

.action-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #4a5568;
  font-size: 0.95rem;
}

.info-icon {
  color: #3182ce;
}

.action-buttons {
  display: flex;
  gap: 0.75rem;
  margin-left: auto;
}

.error-message {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  color: #e53e3e;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #fff5f5;
  border-radius: 0.5rem;
  border-left: 3px solid #e53e3e;
  font-size: 0.95rem;
}

.error-icon {
  margin-top: 0.15rem;
}

</style>